import { Component, EventEmitter, NgZone, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
	DocumentSettingsComponent,
	DocumentSettingsDialogData,
	DocumentSettingsSuccessData,
	defaultDocumentSettingsDialogConfig,
} from '@components/documents-matter-editor/document-settings/document-settings.component';
import { ExternalDocumentUpdate } from '@components/html-editor/html-editor.actions';
import { ICON } from '@core/util/icon';
import { Context, Document, Folder } from '@models/matter';
import { DocumentMargins, PageSize } from '@models/documents';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select, Store } from '@ngxs/store';
import { AuthState } from '@state/auth';
import { BusinessState } from '@state/business';
import { MergedMatterFolderSelectors } from '@state/global/documents/merged-folders';
import { SelectedMatterDocumentsDispatch } from '@state/global/documents/selected-matter-documents.dispatch';
import {
	SelectedDocument,
	SelectedMatterDocumentsState,
} from '@state/global/documents/selected-matter-documents.state';
import { InstanceState } from '@state/instance/instance.state';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { Observable } from 'rxjs';
import { filter, take, pairwise } from 'rxjs/operators';
import { TestHooks } from './documents-matter-editor.hooks';

export enum EditorStatus {
	FAILED,
	READY,
	SAVED,
	SAVING,
}

// rome-ignore lint/suspicious/noExplicitAny: <explanation>
declare const tinyMCE: any;

@UntilDestroy()
@Component({
	selector: 'app-documents-matter-editor',
	templateUrl: './documents-matter-editor.component.html',
	standalone: false,
})
export class DocumentsMatterEditorComponent implements OnInit {
	testHooks = TestHooks;
	@SelectSnapshot(InstanceState.id) private readonly instanceId!: string;
	@SelectSnapshot(AuthState.getUserId) userId!: string;

	defaultDocumentMargins: DocumentMargins;
	defaultDocumentPageSize: PageSize;
	errorMessage: string;
	readonly longErrorMessageMaxLength = 150;
	showLongMessage = false;
	override = true;
	html: string | null = null;
	context: Context | null = null;
	margins: DocumentMargins;
	pageSize: PageSize;
	ICON = ICON;
	documentId: string;
	readonly delay = 2500;
	selectedDocument: Document;

	@Output() workingChange = new EventEmitter<boolean>();

	@ViewSelectSnapshot(BusinessState.getDefaultPageDialogData)
	defaultPageLayout: DocumentSettingsDialogData;
	@SelectSnapshot(MergedMatterFolderSelectors.getAll) folders: Folder[];
	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@Select(SelectedMatterDocumentsState.getViewingDocument)
	viewingDocument$: Observable<SelectedDocument>;

	constructor(
		private selectedDocumentDispatch: SelectedMatterDocumentsDispatch,
		private dialog: MatDialog,
		private zone: NgZone,
		private store: Store,
	) {}

	ngOnInit(): void {
		this.defaultDocumentMargins = this.defaultPageLayout.defaultMargins;
		this.defaultDocumentPageSize = this.defaultPageLayout.defaultPageSize;

		this.viewingDocument$.pipe(untilDestroyed(this)).subscribe((doc) => {
			const isNewDocument = doc?.document?.id !== this.documentId;
			this.documentId = doc?.document?.id;
			this.setErrorMessage(doc?.errorMessage);
			const requiresUpdate =
				isNewDocument || (doc?.document?.html && this.override);

			if (!doc) {
				this.html = null;
				this.context = null;
			}

			if (requiresUpdate) {
				this.doUpdateDocument(doc);
			}
		});

		// when the document changes, inspect them and see if the update was from
		// another user, OR, by ourself with a different instance id.
		this.viewingDocument$
			.pipe(
				untilDestroyed(this),
				pairwise(),
				filter(([prev, cur]) => prev?.document?.html !== cur?.document?.html),
			)
			.subscribe(([_, cur]) => {
				const bySameUser = cur?.document?.lastEditedBy === this.userId;
				if (
					!bySameUser ||
					(bySameUser &&
						cur?.document?.lastEditedByInstanceId !== this.instanceId)
				) {
					// prevent when its from a reset/convert or anything else...
					this.store.dispatch(
						new ExternalDocumentUpdate(
							cur.document.id,
							cur.document.html,
							cur.document,
						),
					);
				}
			});
	}

	private doUpdateDocument(doc: SelectedDocument) {
		this.selectedDocument = doc?.document;
		this.html = doc?.document?.html;
		this.context = doc?.document?.context;
		this.override = false;
		if (doc?.document) {
			this.margins = doc.document.margins ?? this.defaultDocumentMargins;
			this.pageSize = doc.document.pageSize ?? this.defaultDocumentPageSize;
		}
	}

	onWorkingChange(working: boolean) {
		this.workingChange.emit(working);
	}

	toggleShowLongMessage(error: string): void {
		this.showLongMessage = !this.showLongMessage;
		this.setErrorMessage(error);
	}

	toggleDocumentSettings(): void {
		this.zone.run(() => {
			this.dialog
				.open<
					DocumentSettingsComponent,
					DocumentSettingsDialogData,
					DocumentSettingsSuccessData
				>(DocumentSettingsComponent, {
					...defaultDocumentSettingsDialogConfig,
					data: {
						defaultMargins: this.defaultPageLayout.defaultMargins,
						defaultPageSize: this.defaultPageLayout.defaultPageSize,
						margins: this.margins,
						pageSize: this.pageSize,
						document: this.selectedDocument,
					},
				})
				.afterClosed()
				.pipe(
					untilDestroyed(this),
					take(1),
					filter((v) => !!v),
				)
				.subscribe((value) => {
					this.selectedDocumentDispatch.SetDocumentSettings(
						this.documentId,
						value.pageSize,
						value.margins,
					);
					this.pageSize = value.pageSize;
					this.margins = value.margins;
				});
		});
	}

	updateDocument(html: string): void {
		this.selectedDocumentDispatch.UpdateSelectedDocumentHtml(
			this.documentId,
			html,
		);
	}

	private setErrorMessage(error: string): void {
		if (
			error?.length > this.longErrorMessageMaxLength &&
			!this.showLongMessage
		) {
			this.errorMessage = `${error?.slice(0, this.longErrorMessageMaxLength)}...`;
		} else {
			this.errorMessage = error;
		}
	}
}
